<script setup lang="ts">
import { onBeforeUnmount, ref, toRef, watch } from 'vue'
import { provideConversationTypography } from '@/components/chat/useConversationTypography'

const props = withDefaults(defineProps<{ fontFamily?: string }>(), { fontFamily: 'Georgia, serif' })
provideConversationTypography(toRef(props, 'fontFamily'))

const root = ref<HTMLElement | null>(null)
const composer = ref<HTMLElement | null>(null)
let observer: ResizeObserver | null = null

function measureComposer() {
  if (root.value && composer.value) {
    root.value.style.setProperty('--composer-band-height', `${composer.value.offsetHeight}px`)
  }
}

watch(composer, (band) => {
  observer?.disconnect()
  observer = null
  if (!band) return
  measureComposer()
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(measureComposer)
    observer.observe(band)
  }
}, { flush: 'post' })
onBeforeUnmount(() => observer?.disconnect())
</script>

<template>
  <div ref="root" class="conversation-frame relative flex flex-col w-screen min-h-screen h-screen">
    <slot />
    <main v-if="$slots.timeline || $slots.composer" class="conversation-content relative flex flex-col flex-1 min-h-0 min-w-0">
      <slot name="timeline" />
      <div v-if="$slots.composer" ref="composer" class="send-msg py-4 md:py-3.5">
        <div class="mx-auto w-full max-w-[48rem] send-msg__inner">
          <slot name="composer" />
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped>
.conversation-frame {
  --input-bar-offset: calc(env(safe-area-inset-bottom, 0px) + 14px);
  --composer-band-height: 96px;
  --conversation-side-padding: 16px;
  height: 100dvh;
  min-height: 100dvh;
}

.conversation-content {
  padding-top: var(--chrome-height, 56px);
}

.send-msg {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  padding-inline: var(--conversation-side-padding);
  padding-bottom: var(--input-bar-offset);
}

.send-msg__inner { pointer-events: auto; }

@media (max-width: 768px) {
  .send-msg { padding-bottom: calc(var(--input-bar-offset) + 6px); }
}

@media (min-width: 769px) {
  .conversation-frame { --conversation-side-padding: 24px; }
}
</style>
